import { useEffect, useState, useCallback, useRef } from 'react';
import api from '../services/api';
import { Button } from './ui/button';
import { Stamp, PenLine, Upload, Trash2, Loader2 } from 'lucide-react';

const KINDS = [
  { kind: 'stamp', title: 'Cachet de l\'entreprise', hint: 'PNG à fond transparent recommandé, 2 Mo max', Icon: Stamp },
  { kind: 'signature', title: 'Signature du gérant', hint: 'PNG à fond transparent recommandé, 2 Mo max', Icon: PenLine },
];

export function CompanyAssets() {
  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-sm" data-testid="company-assets">
      <div className="p-6 border-b border-slate-100">
        <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2"><Stamp className="w-5 h-5 text-[#0A3D73]" /> Cachet et signature</h3>
        <p className="text-sm text-slate-500 mt-1">Apposés sur les factures, proforma et bons de livraison quand vous cochez l'option sur le document</p>
      </div>
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        {KINDS.map((k) => <AssetCard key={k.kind} {...k} />)}
      </div>
    </div>
  );
}

function AssetCard({ kind, title, hint, Icon }) {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/settings/company/assets/${kind}`, { responseType: 'blob' });
      setUrl((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(data); });
    } catch { setUrl(null); }
  }, [kind]);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setMsg(null);
    const fd = new FormData(); fd.append('file', file);
    try {
      await api.post(`/settings/company/assets/${kind}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMsg({ ok: true, text: 'Image enregistrée' }); load();
    } catch (err) { setMsg({ ok: false, text: err.response?.data?.detail || 'Erreur lors de l\'envoi' }); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ''; }
  };

  const remove = async () => {
    setBusy(true); setMsg(null);
    try { await api.delete(`/settings/company/assets/${kind}`); setUrl(null); setMsg({ ok: true, text: 'Image supprimée' }); }
    catch (err) { setMsg({ ok: false, text: err.response?.data?.detail || 'Erreur' }); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid={`asset-card-${kind}`}>
      <p className="text-sm font-medium text-slate-700 flex items-center gap-2"><Icon className="w-4 h-4 text-[#0A3D73]" /> {title}</p>
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files?.[0]); }}
        className="h-40 rounded-md border-2 border-dashed border-slate-300 bg-[linear-gradient(45deg,#f8fafc_25%,transparent_25%,transparent_75%,#f8fafc_75%),linear-gradient(45deg,#f8fafc_25%,#fff_25%,#fff_75%,#f8fafc_75%)] bg-[length:16px_16px] bg-[position:0_0,8px_8px] flex items-center justify-center overflow-hidden"
      >
        {url ? <img src={url} alt={title} className="max-h-36 max-w-full object-contain" data-testid={`asset-preview-${kind}`} /> : <span className="text-xs text-slate-400">{hint}</span>}
      </div>
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`asset-input-${kind}`} />
      <div className="flex items-center gap-2">
        <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()} className="border-slate-300" data-testid={`asset-upload-${kind}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}{url ? 'Remplacer' : 'Importer'}
        </Button>
        {url && <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={remove} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`asset-delete-${kind}`}><Trash2 className="w-3.5 h-3.5 mr-1.5" />Supprimer</Button>}
        {msg && <span className={`text-xs ${msg.ok ? 'text-emerald-600' : 'text-red-600'}`}>{msg.text}</span>}
      </div>
    </div>
  );
}
